import { InternalApiListSelectorGroup } from '../../../api-internal';
import { useState } from 'react';
import { SelectorOptionMap, SelectorOptionType } from './Selector';
import { ManualCustomListSelectorCode, selectorConfigByType } from './ListGroups';

interface Props {
  type: ManualCustomListSelectorCode;
  initialFirstGroup: InternalApiListSelectorGroup | undefined;
  initialFirstSelectorOption: SelectorOptionType;
  applyGroupToSelectorOptions: (group: InternalApiListSelectorGroup | undefined) => SelectorOptionMap;
  optionDepth: number;
  clearChildrenFilters: (level: number) => void;
}

export const useSelectorNavigation = ({
  type,
  initialFirstGroup,
  initialFirstSelectorOption,
  applyGroupToSelectorOptions,
  optionDepth,
  clearChildrenFilters,
}: Props) => {
  const getCanceledChildrenNavigation = (prevNavigationIndex: string[], level: number) => {
    for (let i = level; i < prevNavigationIndex.length; i++) {
      prevNavigationIndex[i] = '';
    }
    return [...prevNavigationIndex];
  };

  const getNavigationIndexFromGroup = (
    prevNavigationIndex: string[],
    group: InternalApiListSelectorGroup | undefined,
    firstSelectorOption: SelectorOptionType
  ): string[] => {
    prevNavigationIndex[0] = group?.code ?? '';
    return getNavigationIndexFromOption(prevNavigationIndex, firstSelectorOption, 1);
  };

  const getNavigationIndexFromOption = (
    prevNavigationIndex: string[],
    option: SelectorOptionType,
    level: number
  ): string[] => {
    prevNavigationIndex[level] = option.value;
    if (option.children.length) {
      return getNavigationIndexFromOption(prevNavigationIndex, option.children[0], level + 1);
    } else {
      return getCanceledChildrenNavigation(prevNavigationIndex, level);
    }
  };

  const [navigationIndex, setNavigationIndex] = useState(() => {
    return getNavigationIndexFromGroup(
      new Array(1 + optionDepth).fill(''),
      initialFirstGroup,
      initialFirstSelectorOption
    );
  });

  const navigateFromGroup = (group: InternalApiListSelectorGroup | undefined) => {
    const selectorOptionsWithGroup = applyGroupToSelectorOptions(group);
    const { rootOptionValue } = selectorConfigByType[type];
    const newNavigationIndex = getNavigationIndexFromGroup(
      navigationIndex,
      group,
      selectorOptionsWithGroup.get(rootOptionValue)!.children[0]
    );
    setNavigationIndex(newNavigationIndex);
    clearChildrenFilters(0);
  };

  const navigateFromOption = (option: SelectorOptionType, level: number) => {
    const newNavigationIndex = getNavigationIndexFromOption([...navigationIndex], option, level);
    setNavigationIndex(newNavigationIndex);
    clearChildrenFilters(level);
  };

  const cancelChildrenNavigation = (level: number) => {
    setNavigationIndex(getCanceledChildrenNavigation(navigationIndex, level));
  };

  return {
    navigationIndex,
    navigateFromGroup,
    navigateFromOption,
    cancelChildrenNavigation,
  };
};
